import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Send } from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button/Button";
import { useNotification } from "../common/NotificationProvider";
import { regularizationReasonService } from "../../api/crm/config/configService";
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from "../../utils/formValidation";

const RegularizationRequestModal = ({
  isOpen,
  onClose,
  onSuccess,
  prefilledData = null,
  disableEmployeeSelect = false,
}) => {
  const { showSuccess, showError } = useNotification();

  const [formData, setFormData] = useState({
    date: "",
    checkIn: "",
    checkOut: "",
    reason: "",
    reasonId: "",
    description: "",
    employeeId: "",
  });

  const [formErrors, setFormErrors] = useState({});

  // Searchable employee dropdown states
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);

  // Debounce employee search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Handle prefilled data or reset on open/close
  useEffect(() => {
    if (isOpen) {
      if (prefilledData) {
        let formattedDate = "";
        if (prefilledData.date) {
          try {
            const d = new Date(prefilledData.date);
            if (!isNaN(d.getTime())) {
              formattedDate = d.toISOString().split('T')[0];
            } else {
              formattedDate = prefilledData.date;
            }
          } catch (e) {
            formattedDate = prefilledData.date;
          }
        }

        const formatTimeToHHMM = (timeStr) => {
          if (!timeStr || timeStr === 'N/A' || typeof timeStr !== 'string') return "";
          const match = timeStr.match(/^(\d{2}):(\d{2})/);
          if (match) {
            return `${match[1]}:${match[2]}`;
          }
          return "";
        };

        const checkInTime = formatTimeToHHMM(prefilledData.checkIn);
        const checkOutTime = formatTimeToHHMM(prefilledData.checkOut);

        setFormData({
          employeeId: prefilledData.employeeId || "",
          date: formattedDate || "",
          checkIn: checkInTime || "",
          checkOut: checkOutTime || "",
          reason: "",
          reasonId: "",
          description: "",
        });

        if (prefilledData.employeeData) {
          setSelectedEmployeeData(prefilledData.employeeData);
        } else if (prefilledData.employeeId) {
          setSelectedEmployeeData({
            _id: prefilledData.employeeId,
            employeeId: prefilledData.employeeCode || "",
            personalInfo: {
              firstName: prefilledData.employeeName?.split(' ')[0] || "",
              lastName: prefilledData.employeeName?.split(' ').slice(1).join(' ') || "",
            },
            employmentInfo: {
              department: prefilledData.employeeDepartment || "",
            }
          });
        }
      } else {
        setFormData({
          date: "",
          checkIn: "",
          checkOut: "",
          reason: "",
          reasonId: "",
          description: "",
          employeeId: "",
        });
        setSelectedEmployeeData(null);
      }
      setFormErrors({});
      setEmployeeSearchOpen(false);
      setEmployeeSearchTerm("");
    }
  }, [isOpen, prefilledData]);

  // Fetch attendance times automatically (only if not prefilled)
  useEffect(() => {
    if (formData.employeeId && formData.date && isOpen && !prefilledData) {
      const fetchAttendanceTimes = async () => {
        try {
          const token = localStorage.getItem("auth_token");
          const url = `${process.env.REACT_APP_API_BASE_URL}/regularization-test/attendance-times/fetch?employeeId=${formData.employeeId}&date=${formData.date}`;
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const result = await response.json();
          if (result.success && result.data) {
            const { checkIn, checkOut } = result.data;
            setFormData(prev => ({
              ...prev,
              checkIn: checkIn || "",
              checkOut: checkOut || ""
            }));
          }
        } catch (error) {
          console.error("Error fetching attendance times:", error);
        }
      };

      fetchAttendanceTimes();
    }
  }, [formData.employeeId, formData.date, isOpen, prefilledData]);

  // Employees Query
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['regularization-employees-search-modal', debouncedEmployeeSearch],
    queryFn: async () => {
      const trimmedSearch = debouncedEmployeeSearch?.trim() || "";
      const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(trimmedSearch)}&limit=50&includeSuperAdmin=true`;

      const token = localStorage.getItem("auth_token");
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          return data.data.employees || [];
        }
      }
      return [];
    },
    enabled: employeeSearchOpen,
    staleTime: 0,
  });

  const dropdownEmployees = searchedEmployeesData || [];

  // Reasons Query
  const { data: reasonsData } = useQuery({
    queryKey: ["regularizationReasonsModal", "dropdown"],
    queryFn: () => regularizationReasonService.getDropdown()
  });
  const dynamicReasons = reasonsData?.data || [];
  const hardcodedReasons = [];

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeSearchOpen && !event.target.closest('.employee-dropdown-container')) {
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
      }
    };

    if (employeeSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [employeeSearchOpen]);

  const handleFieldChange = (name, value) => {
    if (name === 'reason') {
      const reasonObj = dynamicReasons.find(r => (r._id || r.id) === value);
      if (reasonObj) {
        setFormData(prev => ({ ...prev, reason: reasonObj.name, reasonId: value }));
      } else {
        setFormData(prev => ({ ...prev, reason: value, reasonId: "" }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    let error = "";
    if (name === 'employeeId' && !value) error = "Employee selection is required";
    if (name === 'date') {
      if (!value) {
        error = "Date is required";
      } else {
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);
        const diffDays = Math.round((today - selectedDate) / (1000 * 60 * 60 * 24));

        if (diffDays > 3) {
          error = "Regularization can only be requested within 3 days of the attendance date";
        } else if (diffDays < 0) {
          error = "Cannot select a future date";
        }
      }
    }
    if (name === 'reason' && !value) error = "Reason is required";
    if (name === 'checkIn' && !value) error = "Check-in time is required";
    if (name === 'checkOut' && !value) error = "Check-out time is required";

    if (name === 'description') {
      error = validateDescription(value);
    }

    if (name === 'checkIn' || name === 'checkOut') {
      const otherField = name === 'checkIn' ? 'checkOut' : 'checkIn';
      const otherValue = formData[otherField];
      if (value && otherValue) {
        const checkInVal = name === 'checkIn' ? value : otherValue;
        const checkOutVal = name === 'checkIn' ? otherValue : value;
        if (checkOutVal <= checkInVal) {
          setFormErrors(prev => ({ ...prev, timeValidation: "Check-out must be after check-in", [name]: "" }));
        } else {
          setFormErrors(prev => ({ ...prev, timeValidation: "", [name]: "" }));
        }
      }
    }

    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'description') {
      processedValue = value.replace(INVALID_CHARS_REGEX, '');
      if (processedValue.length > 500) processedValue = processedValue.slice(0, 500);
    }

    handleFieldChange(name, processedValue);
  };

  const validateDescription = (description) => {
    if (!description || description.trim().length < 10) {
      return "Description must be at least 10 characters";
    }
    if (description.length > 500) {
      return "Description must be no more than 500 characters";
    }
    if (!VALID_TEXT_REGEX.test(description)) {
      return "Description contains invalid characters";
    }
    return null;
  };

  const handleTextKeyPress = (e) => {
    // Sanitization is handled by handleInputChange
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setFormErrors({});

    try {
      if (!formData.employeeId) {
        showError("❌ Please select an employee");
        return;
      }

      const requestData = {
        employeeId: formData.employeeId,
        date: formData.date,
        requestedCheckIn: formData.checkIn,
        requestedCheckOut: formData.checkOut,
        reason: formData.reason,
        reasonId: formData.reasonId || null,
        description: formData.description,
        requestType: "both",
        priority: "medium",
      };

      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization-test`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (data.success) {
        showSuccess("Regularization request submitted successfully!");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        showError(data.message || "Failed to submit regularization request");
      }
    } catch (error) {
      showError("An error occurred while submitting regularization request");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New Regularization Request"
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmitRequest} className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {!disableEmployeeSelect ? (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Employee <span className="text-red-500">*</span>
              </label>
              <div className="relative employee-dropdown-container">
                <div
                  onClick={() => {
                    setEmployeeSearchOpen(!employeeSearchOpen);
                    if (!employeeSearchOpen) {
                      setEmployeeSearchTerm("");
                    }
                  }}
                  className={`mt-1 w-full border rounded-md px-3 py-2 bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:ring-0 focus-within:outline-none focus-within:border-brand ${formErrors.employeeId ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                >
                  <span className={formData.employeeId ? "text-gray-900" : "text-gray-500"}>
                    {formData.employeeId && selectedEmployeeData
                      ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""
                      } (${selectedEmployeeData.employeeId || ""})`
                      : "Select Employee"}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${employeeSearchOpen ? "transform rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>

                {employeeSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by employee name..."
                          value={employeeSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setEmployeeSearchTerm(value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          maxLength={100}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto">
                      {isLoadingEmployeeSearch ? (
                        <div className="p-4 text-center text-gray-500 text-sm italic flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                          <span>Loading employees...</span>
                        </div>
                      ) : dropdownEmployees.length > 0 ? (
                        dropdownEmployees.map((employee) => (
                          <div
                            key={employee._id}
                            onClick={() => {
                              handleFieldChange('employeeId', employee._id);
                              setSelectedEmployeeData(employee);
                              setEmployeeSearchOpen(false);
                              setEmployeeSearchTerm("");
                            }}
                            className={`px-4 py-2 hover:bg-brand/5 cursor-pointer border-b border-gray-100 ${formData.employeeId === employee._id ? "bg-brand/5" : ""}`}
                          >
                            <div className="text-sm font-medium text-gray-900">
                              {employee.personalInfo?.firstName || ""} {employee.personalInfo?.lastName || ""}
                            </div>
                            <div className="text-xs text-gray-500">
                              {employee.employeeId || ""} - {employee.employmentInfo?.department || ""}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {debouncedEmployeeSearch.trim() ? "No employees found" : "Type to search employees..."}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {formErrors.employeeId && (
                <p className="mt-1 text-xs text-red-500">{formErrors.employeeId}</p>
              )}
            </div>
          ) : (
            selectedEmployeeData && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                <div className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-50 text-gray-700 text-sm">
                  {selectedEmployeeData.personalInfo?.firstName || ""} {selectedEmployeeData.personalInfo?.lastName || ""}
                  {selectedEmployeeData.employeeId ? ` (${selectedEmployeeData.employeeId})` : ""}
                </div>
              </div>
            )
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              required
              max={new Date().toISOString().split('T')[0]}
              className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.date ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
            />
            {formErrors.date && (
              <p className="mt-1 text-xs text-red-500">{formErrors.date}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <select
              name="reason"
              value={formData.reasonId || formData.reason}
              onChange={handleInputChange}
              required
              className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.reason ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
            >
              <option value="">Select reason</option>
              {dynamicReasons.length > 0 ? (
                dynamicReasons.map((r) => (
                  <option key={r._id || r.id} value={r._id || r.id}>
                    {r.name}
                  </option>
                ))
              ) : (
                hardcodedReasons.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))
              )}
            </select>
            {formErrors.reason && (
              <p className="mt-1 text-xs text-red-500">{formErrors.reason}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check In Time <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              name="checkIn"
              value={formData.checkIn}
              onChange={handleInputChange}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              required
              className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.checkIn || formErrors.timeValidation ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
            />
            {formErrors.checkIn && (
              <p className="mt-1 text-xs text-red-500">{formErrors.checkIn}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check Out Time <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              name="checkOut"
              value={formData.checkOut}
              onChange={handleInputChange}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              required
              className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.checkOut || formErrors.timeValidation ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
            />
            {formErrors.checkOut && (
              <p className="mt-1 text-xs text-red-500">{formErrors.checkOut}</p>
            )}
            {formErrors.timeValidation && (
              <p className="mt-1 text-xs text-red-500">{formErrors.timeValidation}</p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description <span className="text-red-500">*</span> <span className="text-gray-500 text-xs">(10-500 characters)</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            onKeyPress={handleTextKeyPress}
            required
            rows={4}
            maxLength={500}
            placeholder="Please provide detailed explanation..."
            className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
          />
          <div className="flex justify-between items-center mt-1">
            {formErrors.description ? (
              <p className="text-sm text-red-600">{formErrors.description}</p>
            ) : (
              <div></div>
            )}
            <span className="text-xs text-gray-500">
              {formData.description.length}/500
            </span>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
          >
            Cancel request
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex items-center space-x-2"
          >
            <Send className="h-4 w-4" />
            <span>Submit regularisation request</span>
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RegularizationRequestModal;
